import classNames from 'classnames'
import { View } from 'components/View'
import { KEYBOARD_KEY_CODE } from 'constants/keyboard-key'
import {
  MESSAGE_TYPE,
  MESSAGE_TYPE_TO_COLOR
} from 'constants/message'
import { STYLES_COLOR } from 'modules/styles/constants/index.cjs'
import PropTypes from 'prop-types'
import React from 'react'
import getStyleComponentUtil from 'modules/component/utils/get-style'
import { formatDatetimeUtil } from 'utils/datetime'
import './index.scss'
import { usePreviousHookUtil } from 'modules/component/utils/hooks/usePrevious.js'
import Row from 'components/Row.js'
import { logUtil } from 'packages/src'

/**
 * The available input types.
 * @type {Object}
 */
const TYPE = {
  DATE: 'date',
  NUMBER: 'number',
  EMAIL: 'email',
  PASSWORD: 'password',
  SEARCH: 'search',
  TEL: 'tel',
  TEXT: 'text'
}

const BEFORE = {
  AT: '@'
}

/**
 * Types that should only handle number characters.
 * @type {Array}
 */
const NUMBER_TYPES = [
  TYPE.NUMBER,
  TYPE.TEL
]

export default function Input (props) {
  const {
    colorType,
    type = TYPE.TEXT,
    onFocus,
    value: propValue
  } = props
  const [value, setValue] = React.useState(propValue || '')
  const [isFocused, setIsFocused] = React.useState(props.isFocused)
  const prevIsFocused = usePreviousHookUtil(isFocused)
  const prevColorType = usePreviousHookUtil(colorType)
  const ref = React.useRef()

  const handleFormatValueOnMount = () => {
    // If the value is a `date`, format it
    if (type !== TYPE.DATE || !propValue) {
      return
    }
    setValue(formatDatetimeUtil(propValue))
  }

  const focus = () => {
    if (!ref.current) {
      logUtil('Input#focus').debug('[noop] no ref')
      return
    }

    if (props.isSelectedOnFocus) {
      logUtil('Input#focus').debug('[input][select]')
      ref.current.select()
      return
    }

    logUtil('Input#focus').debug('[done]')
    ref.current.focus()
  }

  React.useEffect(() => {
    handleFormatValueOnMount()
  }, [])

  React.useEffect(() => {
    if (!prevIsFocused && isFocused) {
      focus()
    }
  }, [
    prevIsFocused,
    isFocused
  ])

  React.useEffect(() => {
    if (propValue !== value) {
      setValue(propValue)
    }
  }, [propValue])

  React.useEffect(() => {
    if (
      prevColorType !== MESSAGE_TYPE.ERROR &&
      colorType === MESSAGE_TYPE.ERROR
    ) {
      focus()
    }
  }, [
    prevColorType,
    colorType
  ])

  /**
   * Returns true if the given input type should not handle certain characters.
   * e.g. input type `tel` should not handle other characters than numbers
   * @param {number} key
   * @returns {boolean}
   */
  const shouldPreventDefaultLocal = (key) => {
    const { type } = props
    // If the input is of type number, allow the character '.'
    if (
      type === TYPE.NUMBER &&
      String.fromCharCode(key) === '.'
    ) {
      return false
    }
    // If the input is of type 'number' or 'tel',
    // but the `key` is not numeric:
    // return false
    if (
      NUMBER_TYPES.includes(type) &&
      (key < 48 || key > 57)
    ) {
      return true
    }
    return false
  }

  const handleChange = (event) => {
    const newValue = event.target.value
    const name = props.name

    if (props.maxLength && newValue.length > props.maxLength) {
      return
    }

    if (props.onChange) {
      props.onChange(event, name, newValue)
    }
    setValue(newValue)
  }

  const handleKeyDown = (event) => {
    event.stopPropagation()
    const key = event.which || event.keyCode
    const onEnter = props.onEnter
    const { shouldPreventDefault } = props
    if (
      shouldPreventDefault &&
      shouldPreventDefault(key, value)
    ) {
      event.preventDefault()
      return
    }
    if (key !== KEYBOARD_KEY_CODE.ENTER || !onEnter) {
      return
    }
    onEnter(event, { value })
  }

  const handleKeyPress = (event) => {
    // Prevent to trigger a keypress on the `window`
    const key = event.which || event.keyCode
    event.stopPropagation()
    const { shouldPreventDefault } = props
    if (
      shouldPreventDefaultLocal(key) ||
      (
        shouldPreventDefault &&
        shouldPreventDefault(key, value)
      )
    ) {
      event.preventDefault()
    }
  }

  const getColor = () => {
    return props.color || STYLES_COLOR.BLACK
  }

  const getBorderColor = () => {
    const {
      color,
      colorType
    } = props
    const borderColor = MESSAGE_TYPE_TO_COLOR[colorType]
    if (borderColor) {
      return borderColor
    }
    if (color && color !== STYLES_COLOR.BLACK) {
      return color
    }
    return STYLES_COLOR.GREY_MEDIUM
  }

  const triggerFocus = () => {
    logUtil('Input#triggerFocus').debug('[start]')
    setIsFocused(true)
    onFocus?.(props.name)
  }

  const handleClick = () => {
    logUtil('Input#handleClick').debug('[start]')
    triggerFocus()
    focus()
  }

  const handleFocus = () => {
    logUtil('Input#handleFocus').debug('[start]')
    triggerFocus()
  }

  const renderField = () => {
    if (
      props.placeholder &&
      !isFocused &&
      !value
    ) {
      return
    }

    const styleToPass = getStyleComponentUtil({
      // paddingTop: '0.5em',
      height: '100%',
      width: '100%',
      boxSizing: 'border-box !important',
      overflow: 'hidden',
      opacity: props.isDisabled ? 0.5 : 1,
      fontFamily: 'inherit'
    }, props.inputStyle)

    const propsToPass = {
      disabled: props.isDisabled,
      maxLength: props.maxLength || props.size,
      size: props.size,
      type,
      style: styleToPass,
      className: 'input-field',
      name: props.name,
      ref,
      value,
      step: type === TYPE.NUMBER ? '0.1' : null,
      onChange: handleChange,
      onFocus: handleFocus,
      onKeyDown: handleKeyDown,
      onKeyPress: handleKeyPress
    }

    return (
      <input {...propsToPass} onBlur={props.onBlur} />
    )
  }

  const renderLetterCount = () => {
    if (
      !props.hasLetterCount ||
      !value
    ) {
      return
    }
    return (
      <span className='input-letterCount'>
        {value.length}
      </span>
    )
  }

  const renderPlaceHolder = () => {
    if (!props.placeholder) {
      return
    }
    const className = classNames({
      'input-placeholder': true,
      'input-placeholder_small': !!(isFocused || value)
    })
    return (
      <div className={className}>
        {props.placeholder}
      </div>
    )
  }

  const className = classNames({
    input: true,
    input_paddingRight: props.hasLetterCount,
    input_disabled: props.isDisabled,
    input_noPadding: props.hasNoPadding,
    input_smallHeight: props.isSmallHeight
  })

  return (
    <div
      className={className}
      data-ui-before={props.before}
      onClick={handleClick}
      style={getStyleComponentUtil({
        color: getColor(),
        border: `0.055em solid ${getBorderColor()}`,
        fontSize: props.isBig ? '1.5em' : '1em'
      }, props.style)}
    >
      <View
        style={{
          width: '100%'
        }}
      >
        {renderPlaceHolder()}
        <Row ac>
          {renderField()}
          {renderLetterCount()}
          {props.rightElement}
        </Row>
      </View>
    </div>
  )
}

Input.BEFORE = BEFORE
Input.COLOR = STYLES_COLOR
Input.COLOR_TYPE = MESSAGE_TYPE
Input.TYPE = TYPE

Input.propTypes = {
  name: PropTypes.string,
  before: PropTypes.oneOf(
    Object.values(BEFORE)
  ),
  /**
   * The type.
   * e.g. 'radio'
   * @type {string}
   */
  type: PropTypes.oneOf(
    Object.values(Input.TYPE)
  ),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool
  ]),
  /**
   * The placeholder.
   * e.g. 'emailExample@gmail.com'
   * @type {string}
   */
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  size: PropTypes.number,
  /**
   * Colors used for border-bottom.
   * e.g. 'green'
   * @type {string}
   */
  color: PropTypes.oneOf(
    Object.values(Input.COLOR)
  ),
  /**
   * Color type used to define a color.
   * e.g. `success` => `green`
   * @type {string}
   */
  colorType: PropTypes.oneOf(
    Object.values(Input.COLOR_TYPE)
  ),
  isFocused: PropTypes.bool,
  isSelectedOnFocus: PropTypes.bool,
  isDisabled: PropTypes.bool,
  hasLetterCount: PropTypes.bool,
  hasNoPadding: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onEnter: PropTypes.func,
  shouldPreventDefault: PropTypes.func,
  isSmallHeight: PropTypes.bool,
  isBig: PropTypes.bool,
  style: PropTypes.object
}
